import { Component, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { PatientLogin, UserService } from "@main/users";
import { PatientService } from "../patient-switch/patient.service";
import { IncompleteDataService } from "@main/incomplete-data/incomplete-data.service";
import { IncompleteDataRequestsService } from "@main/incomplete-data/incomplete-data-requests.service";
import { OrganizationShareReviewed } from "../patient-switch/organization-share-reviewed";
import { OrganizationService } from "@main/companies";

@Component({
    selector: "data-sharing-message",
    templateUrl: "./data-sharing-message.component.html",
    styleUrls: ["./data-sharing-message.component.scss"]
})
export class DataSharingMessageComponent implements OnInit {
    visible: boolean = false;
    dialogMessage: string = "";
    firstTimeLoginWithNHSLogin: boolean = false;
    buttonUsed: boolean = false;

    constructor(
        private router: Router,
        private userService: UserService,
        private patientService: PatientService,
        private incompleteDataService: IncompleteDataService,
        private incompleteDataRequestsService: IncompleteDataRequestsService,
        private organizationService: OrganizationService,
    ) {}

    async ngOnInit() {
        // If the user need to configure the 2FA. At this point the claims are still not calculated,
        // so instead of use userHelperService.IsPatient, we need to check the user logged directly.
        if (!(this.userService.currentUser as PatientLogin).patientId) {
            return;
        }

        const patientLogin = this.userService.currentUser as PatientLogin;
        const patientId = patientLogin.patientId;
        if (!this.patientService.currentPatientId) {
            this.patientService.currentPatientId = patientId;
        }

        const patientHasNHSLogin = !!patientLogin.nhsNumber;
        const isFirstNhsLogin = patientHasNHSLogin && !patientLogin.loggedInHavingNHSLogin;

        if (isFirstNhsLogin) {
            this.firstTimeLoginWithNHSLogin = true;
        }

        const organisations = await this.patientService.GetOrganizationsShareReviewed(patientId);

        // Classify each org by what consent domains are needed
        const miProOnlyOrgs: string[] = [];
        const nhsOnlyOrgs: string[] = [];
        const bothOrgs: string[] = [];

        for (const org of organisations) {
            const needsMiPro = !org.dataShareReviewed;
            const needsNhs = patientHasNHSLogin && org.hasNhsOn && (!org.nhsWasSwitchedOn || isFirstNhsLogin);

            if (needsMiPro && needsNhs) {
                bothOrgs.push(org.organizationName);
            } else if (needsMiPro) {
                miProOnlyOrgs.push(org.organizationName);
            } else if (needsNhs) {
                nhsOnlyOrgs.push(org.organizationName);
            }
        }

        // Build messages per group
        let message = this.buildGroupMessage(bothOrgs,
            MessageCases.firstOrganisationMiproAndNHSData,
            MessageCases.twoOrganisationsMiProAndNHSData,
            MessageCases.multipleOrganisationsMiProAndNHSData);

        message = this.concatMessages(message, this.buildGroupMessage(miProOnlyOrgs,
            MessageCases.firstOrganisationMiproData,
            MessageCases.twoOrganisationsMiProData,
            MessageCases.multipleOrganisationsMiProData));

        message = this.concatMessages(message, this.buildGroupMessage(nhsOnlyOrgs,
            MessageCases.firstOrganisationNHSData,
            MessageCases.twoOrganisationsNHSData,
            MessageCases.multipleOrganisationsNHSData));

        if (message == "") {
            return;
        }

        this.dialogMessage = `${message}
                            <br>
                            ${MessageCases.generalMessage}
                            <br>
                            <a class="no-decoration" href='/static/privacy-policy' target='_blank'>Click here to view the Privacy Policy</a>`;
        this.visible = true;
    }

    async accept() {
        this.buttonUsed = true;
        await this.updateDataShare(true);

        const externalSiteURL = (await this.organizationService.getExternalSiteURL()).externalSiteURL;
        if(externalSiteURL){
            window.location.href = externalSiteURL;
        }

        this.visible = false;
    }

    remindMeLater() {
        this.visible = false;
    }

    async manage() {
        this.buttonUsed = true;
        this.updateDataShare(false);
        this.router.navigate(["/app/profile/data-sharing"]);
        this.visible = false;
    }

    async updateDataShare(grantAccess: boolean) {
        if (this.firstTimeLoginWithNHSLogin) {
            // Asynchronous.
            this.patientService.updateLoggedInHavingNHSLogin(this.userService.currentUser.id);
            (this.userService.currentUser as PatientLogin).loggedInHavingNHSLogin = true;
        }

        if (grantAccess) {
            await this.incompleteDataRequestsService.grantAutomaticAccessAll();
        }
        // Asynchronous.
        const patientId = (this.userService.currentUser as PatientLogin).patientId
            ?? this.patientService.currentPatientId;
        this.patientService.UpdateDataShareReviewedAsync(patientId, this.firstTimeLoginWithNHSLogin);
    }

    concatMessages(first: string, second: string): string {
        if (first == "") {
            return second;
        }

        if (second == "") {
            return first;
        }

        return first + "<br>" + second;
    }

    buildGroupMessage(orgNames: string[], templateOne: string, templateTwo: string, templateMultiple: string): string {
        if (orgNames.length == 0) return "";
        if (orgNames.length == 1) return this.transformMessage(templateOne, orgNames[0]);
        if (orgNames.length == 2) return this.transformMessage(templateTwo, orgNames[0], orgNames[1]);
        return templateMultiple;
    }

    // Simulating C#'s String.Format
    transformMessage(sentence: string, first: string, second: string = null): string {
        let newMessage = sentence.split("{0}").join(first);
        return second ? newMessage.split("{1}").join(second) : newMessage;
    }
}

enum MessageCases {
    firstOrganisationMiproData = "Would you like to share your Passport Data with {0}?",
    firstOrganisationNHSData = "Would you like to share your NHS Data with {0}?",
    firstOrganisationMiproAndNHSData = "Would you like to share your NHS and Passport Data with {0}?",
    secondOrganisationMiproData = "Would you like to share your Passport Data with {1}?",
    secondOrganisationMiproAndNHSData = "Would you like to share your NHS and Passport Data with {1}?",
    twoOrganisationsMiProData = "Would you like to share your Passport Data with {0} and {1}?",
    twoOrganisationsNHSData = "Would you like to share your NHS data with {0} and {1}?",
    twoOrganisationsMiProAndNHSData = "Would you like to share your NHS and Passport Data with {0} and {1}?",
    multipleOrganisationsMiProData = "Would you like to share your Passport Data with all sending Organisations?",
    multipleOrganisationsNHSData = "Would you like to share your NHS Data with all sending Organisations?",
    multipleOrganisationsMiProAndNHSData = "Would you like to share your NHS and Passport Data with all sending Organisations?",
    generalMessage = "To manage your data-sharing preferences, please click the Manage button below or go to the Data Sharing tab in My Profile to review your settings. "
}
